import type { TFunction } from 'i18next';
import type { AutoWithdrawalNetworkCode } from '@shared/api/contracts';
import type { WithdrawalNetwork } from '@shared/config/networks';
import { clampNonNegative, formatAmount } from '@shared/lib/format-amount';
import type { CryptoAddressValidationErrorCode } from '@shared/lib/validate-crypto-address';
import { describeAddressFeedback } from './address-feedback';

const MANUAL_NETWORK: Partial<Record<AutoWithdrawalNetworkCode, WithdrawalNetwork>> = {
  'TRC20': 'TRC-20',
  'ERC20': 'ERC-20',
  'BEP20': 'BEP-20',
};

export interface WithdrawalAddressFeedback {
  message: string;
  suggestedNetwork: WithdrawalNetwork | null;
}

export const sanitizeWithdrawalAmountInput = (value: string): string => {
  const cleaned = value.replace(/\s/g, '').replace(/[^\d.,]/g, '').replace(',', '.');
  const dot = cleaned.indexOf('.');
  if (dot === -1) return cleaned;
  const integer = cleaned.slice(0, dot);
  const fraction = cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 8);
  return `${integer}.${fraction}`;
};

export const sanitizeWithdrawalAddressInput = (value: string): string => value.replace(/\s/g, '');

export const formatMaxWithdrawalAmount = (balance: number): string => {
  if (!Number.isFinite(balance) || balance <= 0) return '';
  return formatAmount(balance).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};

/** Manual USDT payout uses the spendable network balance. Null stays unknown. */
export const getManualWithdrawalCap = (networkBalance: number | null): number | null => {
  if (networkBalance == null || Number.isNaN(networkBalance)) return null;
  return clampNonNegative(networkBalance);
};

export const toManualNetwork = (network: AutoWithdrawalNetworkCode | null): WithdrawalNetwork | null =>
  network ? MANUAL_NETWORK[network] ?? null : null;

export const describeWithdrawalAddressError = (
  address: string,
  network: string,
  errorCode: CryptoAddressValidationErrorCode | undefined,
  t: TFunction,
): WithdrawalAddressFeedback => {
  const feedback = describeAddressFeedback(address, network, errorCode, t);
  return {
    message: feedback.message,
    suggestedNetwork: toManualNetwork(feedback.suggestedNetwork),
  };
};
